"use client";

import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../providers/AuthProvider";
import { apiFetch } from "../lib/api";

const VAPID_PUBLIC_KEY = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;

/**
 * Convert a base64url-encoded VAPID key to a Uint8Array
 * (required by PushManager.subscribe).
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

/** Platform detection helpers */
function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as unknown as Record<string, unknown>).standalone === true
  );
}

export type NotificationState =
  | "unsupported"    // Browser doesn't support push
  | "vapid-missing"  // VAPID key not configured
  | "ios-needs-install" // iOS Safari — must install PWA first
  | "denied"         // User blocked notifications at OS level
  | "prompt"         // Can ask for permission
  | "subscribed"     // Actively subscribed
  | "unsubscribed";  // Permission granted but not subscribed

export function useNotifications() {
  const { token } = useAuth();
  const [state, setState] = useState<NotificationState>("prompt");
  const [loading, setLoading] = useState(false);

  // Compute initial state
  useEffect(() => {
    if (typeof window === "undefined") return;

    if (!("Notification" in window) || !("serviceWorker" in navigator)) {
      setState("unsupported");
      return;
    }

    if (!VAPID_PUBLIC_KEY) {
      setState("vapid-missing");
      return;
    }

    if (Notification.permission === "denied") {
      setState("denied");
      return;
    }

    // Check if already subscribed
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => {
        setState(sub ? "subscribed" : "prompt");
      })
      .catch(() => setState("prompt"));
  }, []);

  /** Subscribe to push notifications. */
  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!token || !VAPID_PUBLIC_KEY) return false;

    // iOS push requires standalone mode (installed PWA)
    if (isIOS() && !isStandalone()) {
      setState("ios-needs-install");
      return false;
    }

    setLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState("denied");
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY).buffer as ArrayBuffer,
      });

      // Send subscription to server
      await apiFetch("/push/subscribe", token, {
        method: "POST",
        body: JSON.stringify({
          subscription: subscription.toJSON(),
        }),
      });

      setState("subscribed");
      return true;
    } catch (err) {
      console.error("Push subscription failed:", err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  /** Unsubscribe from push notifications. */
  const unsubscribe = useCallback(async (): Promise<void> => {
    if (!token) return;

    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await apiFetch("/push/subscribe", token, {
          method: "DELETE",
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        });
        await subscription.unsubscribe();
      }
      setState("prompt");
    } catch (err) {
      console.error("Push unsubscribe failed:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  return { state, subscribe, unsubscribe, loading };
}
